import { client } from './api';

/**
 * The fax inbox as one list.
 *
 * A transmission that arrived and a document somebody assembled out of it are
 * one kind of thing to the person working the queue: something with a name and
 * some pages, to be tidied up, stapled together and turned into a referral.
 * They live in two tables for good reasons, and that is the system's business
 * rather than the reader's.
 *
 * ``kind`` is the only place the split is still visible, and it is here because
 * acting on a row needs it: renaming, merging and editing all reach a different
 * route depending on which table the row came from.
 */

export type FaxItemKind = 'arrival' | 'document';

export interface FaxItem {
  kind: FaxItemKind;
  id: string;
  /**
   * Null until somebody names it. Deliberately not filled in with a generated
   * title, because the screen needs to tell a name apart from a stand-in it
   * drew itself when it comes to offering a rename.
   */
  name: string | null;
  page_count: number | null;
  received_at: string;
  status: string;
  /** Only an arrival has one: a document is not from anywhere in particular. */
  fax_number_from: string | null;
  /**
   * The physician that number belongs to, when it is exactly one the caller
   * can see. Null when it names nobody or several, so the number stands alone.
   */
  sender_physician_name?: string | null;
  /** Only a document has one. An edit refuses a stale version. */
  version: number | null;
  /** An arrival every page of which already feeds a document. */
  filed: boolean;
  /**
   * The newest referral this document was copied into, when it has been, and
   * that referral's own processing status. Only a document can have started
   * one, and both stay null for a caller who may not read a referral.
   */
  intake_id?: string | null;
  intake_processing_status?: string | null;
  /**
   * The order this fax came back to answer, when a mark on it matched one.
   * Only an arrival has one, and only once the match has run.
   */
  answers_order_id?: string | null;
  /**
   * Whether that order is still waiting on somebody to say the page is
   * signed. False once anybody has answered, so the question is asked once.
   */
  answers_order_awaiting_decision?: boolean;
  /**
   * That order's status, so a link can open the tab holding it. Answering the
   * question moves the order, so the tab is not fixed.
   */
  answers_order_status?: string | null;
  /** What kind of document it is, from Roger's list. */
  fax_type_id?: string | null;
  fax_type_name?: string | null;
  /** Whose it is. A record, so nothing is copied into the patient's chart. */
  patient_id?: string | null;
  /** How a person recognises that patient. Null when the caller may not see them. */
  patient_label?: string | null;
  admission_id?: string | null;
  /** The draft referral it is being gathered for, when there is no patient. */
  intake_draft_id?: string | null;
  intake_draft_name?: string | null;
  /** The agency's own labels, named so the list does not need a second call. */
  tags?: { id: string; name: string }[];
}

export interface FaxItemPage {
  items: FaxItem[];
  total: number;
}

/** The document a merge produced, as the server describes it. */
export interface MergedDocument {
  id: string;
  name: string;
  page_count: number;
  version: number;
}

/** One thing to merge, named the way the list names it. */
export interface MergeItemRef {
  kind: FaxItemKind;
  id: string;
}

export async function listFaxItems(
  limit = 25,
  offset = 0,
  includeFiled = false,
  status?: string,
  faxNumberFrom?: string,
  patientId?: string,
  faxTypeId?: string
): Promise<FaxItemPage> {
  const response = await client.get<FaxItemPage, unknown, true>({
    query: {
      limit,
      offset,
      include_filed: includeFiled,
      status,
      fax_number_from: faxNumberFrom,
      patient_id: patientId,
      fax_type_id: faxTypeId,
    },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-items',
  });
  return response.data;
}

export async function renameFaxItem(
  item: MergeItemRef,
  name: string
): Promise<FaxItem> {
  const response = await client.patch<FaxItem, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-items/${item.kind}/${item.id}`,
  });
  return response.data;
}

/**
 * Staple several things in the list into one document.
 *
 * Whole items in the order given, rather than chosen pages: picking pages is
 * what the editor is for, and this is the bulk move somebody makes from the
 * list with several rows ticked. Merging a single item is allowed and is how
 * an arrival becomes something editable without its own pages being rewritten.
 */
export async function mergeFaxItems(
  name: string,
  items: MergeItemRef[]
): Promise<MergedDocument> {
  const response = await client.post<MergedDocument, unknown, true>({
    body: { name, items },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-documents/merge',
  });
  return response.data;
}
